import { Box, Button, Chip, Stack, TextField } from '@mui/material'
import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useState } from 'react'

const EMAIL_DOMAIN_REGEX = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/

type CarrierToEmailProps = {
  open: boolean
  defaultEmail?: string
  userEmail?: string | null
  errorOnSending?: boolean
  onRecipientsChange: (recipients: string) => void
}

const CarrierToEmail = ({
  open,
  defaultEmail,
  userEmail,
  errorOnSending,
  onRecipientsChange,
}: CarrierToEmailProps) => {
  const [emails, setEmails] = useState<string[]>([])
  const [value, setValue] = useState('')

  useEffect(() => {
    if (!open) return
    const initial = (defaultEmail ?? '').trim()
    setEmails(initial ? [initial] : [])
    setValue('')
  }, [open, defaultEmail])

  useEffect(() => {
    onRecipientsChange(emails.join(','))
  }, [emails, onRecipientsChange])

  const existingEmails = useMemo(() => {
    const all = [...emails]
    if (userEmail) {
      all.push(userEmail)
    }
    return all.map((email) => email.toLowerCase())
  }, [emails, userEmail])

  const getInputError = (input: string): string | null => {
    const trimmed = input.trim()
    if (!trimmed) return null
    if (existingEmails.includes(trimmed.toLowerCase())) {
      return 'This email address has already been added.'
    }
    return null
  }

  const isEmailValid = (input: string) => {
    const trimmed = input.trim()
    return trimmed.length > 0 && EMAIL_DOMAIN_REGEX.test(trimmed)
  }

  const inputError = getInputError(value)
  const showAddButton = value.trim().length > 0
  const canAddEmail = isEmailValid(value) && !inputError

  const handleAddEmail = () => {
    const newEmail = value.trim()
    if (!newEmail || getInputError(newEmail) || !isEmailValid(newEmail)) return
    setEmails((prev) => [...prev, newEmail])
    setValue('')
  }

  const handleDeleteEmail = (index: number) => {
    setEmails((prev) => prev.filter((_, i) => i !== index))
  }

  return (
    <Box
      sx={{
        borderBottom: '0.8px solid #dedede',
        padding: emails.length > 0 || userEmail ? 2 : 0,
        paddingLeft: 2,
        paddingRight: 2,
      }}
    >
      <Stack direction="row" flexWrap="wrap" mb={1}>
        {userEmail && (
          <Chip
            key={`user-${userEmail}`}
            label={userEmail}
            sx={{
              marginBottom: 1,
              marginLeft: 1,
              backgroundColor: '#9E9E9E',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#9E9E9E' },
            }}
          />
        )}
        {emails.map((email, index) => (
          <Chip
            key={`${email}-${index}`}
            label={email}
            color="primary"
            sx={{ marginBottom: 1, marginLeft: 1 }}
            onDelete={() => handleDeleteEmail(index)}
          />
        ))}
      </Stack>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <TextField
          fullWidth
          variant="standard"
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
          onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleAddEmail()
            }
          }}
          placeholder="Add email addresses..."
          size="small"
          error={!!inputError || (errorOnSending && emails.length === 0)}
          helperText={
            inputError ??
            (errorOnSending && emails.length === 0
              ? 'Please add at least one carrier email address.'
              : '')
          }
          sx={{
            '& .MuiInputBase-root:before': { borderBottom: 'none' },
            '& .MuiInputBase-root:after': { borderBottom: 'none' },
            '& .MuiInputBase-root:hover:not(.Mui-disabled, .Mui-error):before': {
              borderBottom: 'none',
            },
          }}
        />
        {showAddButton && (
          <Button
            variant="contained"
            disabled={!canAddEmail}
            onClick={handleAddEmail}
            sx={{
              borderRadius: '6px',
              textTransform: 'capitalize',
              fontWeight: 700,
              bgcolor: 'primary.main',
              minWidth: '110px',
              flexShrink: 0,
              mt: 0.5,
            }}
          >
            Add Email
          </Button>
        )}
      </Stack>
    </Box>
  )
}

export default CarrierToEmail
